import { useId, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { Scale, X } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui'
import { useSaveDb, ensureSaveDb } from '@/lib/useSaveDb'
import { updateWithVersion, isStaleWriteError } from '@/lib/optimistic'
import { computePalletTotals } from '@/lib/palletCompute'
import { safeDecimal } from '@/lib/numericInput'
import { useTareRecipe } from './usePalletPricing'
import { palletEditGuardRefusal } from '@/lib/packhouseErrorDomains'

// ---------------------------------------------------------------------------
// rateCardLabel — a human name for the rate card / financial_products variant
// behind a pallet, so "incomplete rate card" / "no match" save errors tell the
// operator WHICH card to open in Financial → Rate Card. Prefers pack-code ·
// variant, falls back to pack-code · mark, then the variety. Never empty.
// ---------------------------------------------------------------------------
export function rateCardLabel(parts: {
  packType?: string | null
  variant?:  string | null
  mark?:     string | null
  variety?:  string | null
}): string {
  const code   = parts.packType?.trim()
  const detail = parts.variant?.trim() || parts.mark?.trim()
  const bits   = [code, detail].filter(Boolean)
  if (bits.length) return bits.join(' · ')
  const v = parts.variety?.trim()
  return v ? `${v} rate card` : 'the rate card'
}

// ---------------------------------------------------------------------------
// WeighPalletDialog — capture a shell's gross weight + measured tare (the
// weight slice of NewPalletForm), compute net via the shared util, and clear
// awaiting_weight. The DB underweight trigger (mig 107) re-checks on the UPDATE.
// ---------------------------------------------------------------------------
/** The slice of a pallet the dialog reads: both Pallets.tsx's and PalletDetail's rows fit it. */
export interface WeighablePallet {
  pallet_id:       string
  run_id:          string
  version:         number
  product_id:      string | null
  pack_type:       string | null
  grade:           string | null
  size_code:       string | null
  mark:            string | null
  gross_weight_kg: number | null
  carton_count:    number | null
  /** Measured tare per variable component from the last weigh; pre-fills a correction. */
  tare_overrides?: Record<string, number> | null
}

type DialogMode = 'weigh' | 'correct'

/** The words and the extra column patch that tell the two modes apart (kept out of the component's branch count). */
const MODE_COPY: Record<DialogMode, { title: string; button: string; patch: { awaiting_weight?: boolean } }> = {
  weigh:   { title: 'Weigh',          button: '✓ Save weight',     patch: { awaiting_weight: false } },
  correct: { title: 'Correct weight', button: '✓ Save correction', patch: {} },
}

const numText = (n: number | null) => (n != null ? String(n) : '')

/** The measured tare saved at the last weigh, as the inputs the dialog edits (a correction must not force a re-measure). */
function tareInputsFrom(o: Record<string, number> | null | undefined): Record<string, string> {
  return Object.fromEntries(Object.entries(o ?? {}).filter(([, v]) => Number.isFinite(v)).map(([k, v]) => [k, String(v)]))
}

/** What a save wrote, handed to onDone so the caller can tell what changed (the label prints cartons and weight). */
export type WeighSavePatch = { gross_weight_kg: number; net_weight_kg: number | null; carton_count: number }

function saveErrorMessage(e: unknown): string {
  if (isStaleWriteError(e)) return 'This pallet changed elsewhere — close and reopen, then try again.'
  return palletEditGuardRefusal(e) ?? (e instanceof Error ? e.message : 'Save failed')
}

// Moved unchanged from Pallets.tsx, where it was over the gate (cognitive 29); this file is no worse (27).
// fallow-ignore-next-line complexity
export default function WeighPalletDialog({ pallet, mode = 'weigh', onClose, onDone }: {
  pallet: WeighablePallet
  /** 'correct' = the creator fixing a typo within the edit window (U13 R1): same maths, same save, other words. */
  mode?: DialogMode
  onClose: () => void
  onDone: (saved?: WeighSavePatch) => void
}) {
  const saveDb = useSaveDb()
  const uid = useId()
  const qc = useQueryClient()
  const { data: tareRecipe, isSuccess: recipeLoaded, isError: recipeFailed, error: recipeError } = useTareRecipe(pallet.product_id)
  // Same trap as the create form: an unloaded recipe is indistinguishable from
  // an unfinished one downstream (no components → no tare), and computePalletTotals
  // would tell the operator to go ask an admin about a rate card that is fine.
  const recipeUnresolved = !!pallet.product_id && !recipeLoaded
  const { data: prodWeights } = useQuery({
    queryKey: ['pallet-product-weights', pallet.product_id],
    enabled: !!pallet.product_id,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await supabase.from('financial_products')
        .select('min_weight_per_unit_kg, target_weight_per_unit_kg')
        .eq('product_id', pallet.product_id!).maybeSingle()
      return (data ?? null) as { min_weight_per_unit_kg: number | null; target_weight_per_unit_kg: number | null } | null
    },
  })

  const weightSpecs = prodWeights ?? tareRecipe?.weights ?? null

  const [grossKg, setGrossKg]       = useState(numText(pallet.gross_weight_kg))
  const [cartons, setCartons]       = useState(numText(pallet.carton_count))
  const [tareInputs, setTareInputs] = useState<Record<string, string>>(() => tareInputsFrom(pallet.tare_overrides))
  const [saving, setSaving]         = useState(false)
  const [error, setError]           = useState<string | null>(null)

  const gross     = parseFloat(grossKg) || 0
  const cartonQty = parseInt(cartons) || 0
  const variableComponents = (tareRecipe?.components ?? []).filter(c => c.is_variable)

  const cardLabel = rateCardLabel({ packType: pallet.pack_type, mark: pallet.mark })
  const computed = computePalletTotals({
    standard:   tareRecipe?.standard ?? 0,
    components: tareRecipe?.components ?? [],
    cartonQty, gross, tareInputs,
    // A floor seat reads no financial_products row, so the under-weight check falls back to the
    // weight specs the narrow tare read returns (the server trigger is INVOKER and reads no card either).
    variant: weightSpecs ?? null,
    cardLabel,
  })

  async function save() {
    const db = saveDb ?? await ensureSaveDb()
    if (!db) { setError('Session not ready — refresh and try again.'); return }
    if (recipeUnresolved) { setError('The rate card hasn’t loaded yet — check your connection, then try again.'); return }
    if (computed.blockers.length > 0) { setError(computed.blockers.join(' · ')); return }
    if (computed.netWeight == null) { setError(`Could not compute net weight for the "${cardLabel}" rate card — open Financial → Rate Card → "${cardLabel}" and check every line has a tare.`); return }
    setSaving(true); setError(null)
    try {
      await updateWithVersion('pallets', { col: 'pallet_id', value: pallet.pallet_id }, pallet.version, {
        gross_weight_kg: gross,
        net_weight_kg:   computed.netWeight,
        carton_count:    cartonQty,
        tare_overrides:  computed.tareOverrides,
        ...MODE_COPY[mode].patch,
      }, { db: db })
      qc.invalidateQueries({ queryKey: ['pallets', pallet.run_id] })
      onDone({ gross_weight_kg: gross, net_weight_kg: computed.netWeight, carton_count: cartonQty })
    } catch (e) {
      setError(saveErrorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-scrim flex items-end lg:items-center justify-center sm:p-4">
      <div className="bg-canvas w-full sm:max-w-md sm:rounded-xl border border-line-subtle max-h-[92dvh] flex flex-col">
        <header className="flex items-center gap-2 px-4 py-3 border-b border-line-subtle">
          <Scale size={18} className="text-success" />
          <h2 className="font-bold flex-1">{MODE_COPY[mode].title} {pallet.pallet_id}</h2>
          <button onClick={onClose} className="min-w-[44px] min-h-[44px] -m-2 inline-flex items-center justify-center rounded-lg hover:bg-surface" aria-label="Close"><X size={18} /></button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
          <p className="text-caption text-fg-muted">
            {[pallet.pack_type, pallet.grade && `Gr ${pallet.grade}`, pallet.size_code, pallet.mark].filter(Boolean).join(' · ')}
          </p>
          <div>
            <label htmlFor={`${uid}-gross`} className="label">Gross Weight (kg) <span className="text-danger">*</span></label>
            <input id={`${uid}-gross`} type="text" inputMode="decimal" autoFocus className="field text-2xl font-bold"
              placeholder="0.00" value={grossKg} onChange={e => setGrossKg(safeDecimal(e.target.value))} />
          </div>
          <div>
            <label htmlFor={`${uid}-cartons`} className="label">Carton Count <span className="text-danger">*</span></label>
            <input id={`${uid}-cartons`} type="number" inputMode="numeric" className="field text-xl font-bold"
              placeholder="0" value={cartons} onChange={e => setCartons(e.target.value)} />
          </div>

          {/* Measured-tare entry (mig 869) — one input per variable line. */}
          {variableComponents.length > 0 && cartonQty > 0 && (
            <div className="rounded-xl border border-line p-3 space-y-2">
              <p className="text-caption font-semibold text-fg-secondary">Measured tare — weigh each:</p>
              {variableComponents.map(c => (
                <div key={c.component_id} className="flex items-center gap-2">
                  <span id={`${uid}-tare-${c.component_id}`} className="text-sm flex-1">{c.item}</span>
                  <input aria-labelledby={`${uid}-tare-${c.component_id}`} type="text" inputMode="decimal" className="field w-28 text-right"
                    placeholder="kg" value={tareInputs[c.component_id] ?? ''}
                    onChange={e => setTareInputs(prev => ({ ...prev, [c.component_id]: safeDecimal(e.target.value) }))} />
                </div>
              ))}
            </div>
          )}

          {computed.netWeight != null && computed.totalTareKg != null && (
            <p className="text-sm">
              Net weight: <span className={clsx('font-semibold tabular-nums', computed.isUnderweight ? 'text-danger' : 'text-success')}>{computed.netWeight.toFixed(2)} kg</span>
              <span className="text-fg-muted tabular-nums"> (tare {computed.totalTareKg.toFixed(2)} kg)</span>
              {computed.netPerCarton != null && (
                <span className={clsx('block', computed.isUnderweight ? 'text-danger' : 'text-fg-muted')}>
                  {computed.netPerCarton.toFixed(2)} kg/carton
                  {computed.targetPerUnit != null && !computed.isUnderweight && <span className="text-fg-muted"> (target {computed.targetPerUnit} kg)</span>}
                </span>
              )}
            </p>
          )}

          {error && (
            <div className="rounded-xl bg-danger-soft border border-danger-edge px-4 py-3 text-sm text-danger">{error}</div>
          )}

          {recipeUnresolved && (
            <p className="text-caption text-warning">
              {recipeFailed
                ? (recipeError instanceof Error ? recipeError.message : 'The rate card could not be read.')
                : 'Loading the rate card for this pallet — net weight can\'t be worked out until it arrives.'}
            </p>
          )}

          <Button variant="primary" fullWidth className="text-lg"
            disabled={saving || gross <= 0 || cartonQty < 1 || recipeUnresolved || computed.blockers.length > 0}
            onClick={save}>
            {saving ? 'Saving…' : MODE_COPY[mode].button}
          </Button>
        </div>
      </div>
    </div>
  )
}

